// ---------------------------------------------------------------------------
// C5: Tenant Knowledge Base — structured content for AI RAG
// ---------------------------------------------------------------------------
// Source types: manual, website_crawl, document_upload, faq, conversation
// Full-text search plus pgvector-backed semantic retrieval.
// ---------------------------------------------------------------------------

import { pgTable, uuid, varchar, text, boolean, jsonb, timestamp, index, customType } from "drizzle-orm/pg-core";

const tz = { withTimezone: true } as const;
export const KB_EMBEDDING_DIMENSIONS = 768;

const vector = customType<{ data: number[] | null; driverData: string | null }>({
  dataType() {
    return `vector(${KB_EMBEDDING_DIMENSIONS})`;
  },
  toDriver(value) {
    if (!value || value.length === 0) return null;
    return `[${value.join(",")}]`;
  },
  fromDriver(value) {
    if (!value) return null;
    if (Array.isArray(value)) {
      return value.map((entry) => Number(entry));
    }
    return String(value)
      .replace(/^\[/, "")
      .replace(/\]$/, "")
      .split(",")
      .map((entry) => Number(entry.trim()))
      .filter((entry) => Number.isFinite(entry));
  },
});

export const KB_SOURCE_TYPES = ["manual", "website_crawl", "document_upload", "faq", "conversation"] as const;
export type KbSourceType = (typeof KB_SOURCE_TYPES)[number];

export const tenantKnowledgeBase = pgTable(
  "tenant_knowledge_base",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    sourceType: varchar("source_type", { length: 50 }).notNull(), // KbSourceType
    title: varchar("title", { length: 500 }).notNull(),
    content: text("content").notNull(),
    embedding: vector("embedding"),
    embeddingModel: varchar("embedding_model", { length: 50 }),
    embeddedAt: timestamp("embedded_at", tz),
    metadata: jsonb("metadata").$type<{ url?: string; fileName?: string; section?: string; question?: string; category?: string; tags?: string[]; status?: string }>().default({}),
    isActive: boolean("is_active").notNull().default(true),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_kb_tenant_active").on(table.tenantId, table.isActive),
    index("idx_kb_tenant_source").on(table.tenantId, table.sourceType),
  ],
);
